/**
 * The customer details form, built from whatever Internal > Tools > Customer
 * Data Configuration says we ask for.
 *
 * One component serves both places a customer gives us these details: the step
 * during sign-up and the account page afterwards. They differ only in the
 * address lookup and the wording of the button, so keeping them together is what
 * stops the two screens from asking for different things.
 */
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import FormTextInput from "./formInputs/FormTextInput";
import AddressAutocomplete from "./AddressAutocomplete";
import { Address } from "../models/company";
import {
  CUSTOMER_FIELD_LABELS,
  CustomerFieldsConfig,
  CustomerProfile,
  asks,
  emptyAddress,
  requires,
} from "../models/customerFields";

type CustomerDetailsFormProps = {
  config: CustomerFieldsConfig;
  /** What the customer gave us before, if anything. */
  initial?: CustomerProfile;
  submitLabel?: string;
  /** Offer an address search before the fields, as sign-up does. */
  addressLookup?: boolean;
  /** Shown on the address search step only. */
  lookupHeading?: React.ReactNode;
  /** Shown above the fields themselves. */
  fieldsHeading?: React.ReactNode;
  onSubmit: (profile: CustomerProfile) => Promise<boolean | void>;
  /** Optional escape hatch so testers / returning users can enter the app. */
  onSkip?: () => void;
  skipLabel?: string;
};

type Values = {
  name: string;
  companyName: string;
  streetLine: string;
  streetLine2: string;
  city: string;
  postalCode: string;
  stateOrProvinceCode: string;
  countryCode: string;
  mainStreetLine: string;
  mainCity: string;
  mainPostalCode: string;
  mainStateOrProvinceCode: string;
  mainCountryCode: string;
  website: string;
  telephone: string;
  cellphone: string;
  fax: string;
};

const addressValues = (address: Address | null | undefined) => ({
  streetLine: address?.streetLines?.[0] || "",
  streetLine2: address?.streetLines?.[1] || "",
  city: address?.city || "",
  postalCode: address?.postalCode || "",
  stateOrProvinceCode: address?.stateOrProvinceCode || "",
  countryCode: address?.countryCode || "",
});

const addressFrom = (
  streetLine: string,
  streetLine2: string,
  city: string,
  postalCode: string,
  stateOrProvinceCode: string,
  countryCode: string,
): Address | undefined => {
  const lines = [streetLine.trim(), streetLine2.trim()].filter(Boolean);
  if (lines.length === 0 && !city.trim() && !postalCode.trim())
    return undefined;
  return {
    streetLines: lines,
    city: city.trim(),
    postalCode: postalCode.trim(),
    stateOrProvinceCode: stateOrProvinceCode.trim(),
    countryCode: countryCode.trim(),
  };
};

const text = (max: number, label: string, required: boolean) => {
  const schema = Yup.string().max(max, `Must be ${max} characters or less`);
  return required ? schema.required(`${label} is required`) : schema;
};

export default function CustomerDetailsForm(props: CustomerDetailsFormProps) {
  const { config, initial } = props;
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  // Sign-up starts on the lookup; skipped when the customer already has an
  // address on file, since there is nothing to search for.
  const [lookupDone, setLookupDone] = useState(
    !props.addressLookup ||
      !asks(config, "address") ||
      Boolean(initial?.address),
  );
  const [found, setFound] = useState<Address | null>(initial?.address || null);

  if (!lookupDone) {
    return (
      <>
        {props.lookupHeading}
        <AddressAutocomplete
          onAddressSelected={(place) => {
            setFound(place);
            setLookupDone(true);
          }}
          placeholder="Search for your address"
        />
        <p className="text-xs mt-3 text-gray-700">
          Or{" "}
          <a
            className="underline cursor-pointer"
            onClick={() => {
              setFound(emptyAddress());
              setLookupDone(true);
            }}
          >
            enter address manually
          </a>
        </p>
        {props.onSkip && (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => props.onSkip?.()}
              className="text-sm font-medium text-slate-800 underline"
            >
              {props.skipLabel || "Skip for now — go to the platform"}
            </button>
            <p className="mt-1 text-xs text-gray-500">
              You can add your company address later under My information.
            </p>
          </div>
        )}
      </>
    );
  }

  const initialValues: Values = {
    name: initial?.name || "",
    companyName: initial?.companyName || "",
    ...addressValues(found || initial?.address),
    ...{
      mainStreetLine: initial?.mainAddress?.streetLines?.[0] || "",
      mainCity: initial?.mainAddress?.city || "",
      mainPostalCode: initial?.mainAddress?.postalCode || "",
      mainStateOrProvinceCode: initial?.mainAddress?.stateOrProvinceCode || "",
      mainCountryCode: initial?.mainAddress?.countryCode || "",
    },
    website: initial?.website || "",
    telephone: initial?.telephone || "",
    cellphone: initial?.cellphone || "",
    fax: initial?.fax || "",
  };

  const shape: { [key: string]: Yup.AnySchema } = {
    name: text(80, CUSTOMER_FIELD_LABELS.name, requires(config, "name")),
    companyName: text(
      50,
      CUSTOMER_FIELD_LABELS.companyName,
      requires(config, "companyName"),
    ),
    website: text(
      200,
      CUSTOMER_FIELD_LABELS.website,
      requires(config, "website"),
    ),
    telephone: text(
      40,
      CUSTOMER_FIELD_LABELS.telephone,
      requires(config, "telephone"),
    ),
    cellphone: text(
      40,
      CUSTOMER_FIELD_LABELS.cellphone,
      requires(config, "cellphone"),
    ),
    fax: text(40, CUSTOMER_FIELD_LABELS.fax, requires(config, "fax")),
  };

  // An address is only usable with a street, a city, a postal code and a
  // country: a carrier refuses to rate anything less. So when the address is
  // compulsory those four are, individually, compulsory too.
  const addressRequired = requires(config, "address");
  shape.streetLine = text(200, "Address", addressRequired);
  shape.streetLine2 = text(200, "Address line 2", false);
  shape.city = text(50, "City", addressRequired);
  shape.postalCode = text(20, "Postal code", addressRequired);
  shape.stateOrProvinceCode = text(50, "State, province or region", false);
  shape.countryCode = text(50, "Country", addressRequired);

  const mainRequired = requires(config, "mainAddress");
  shape.mainStreetLine = text(200, "Main address", mainRequired);
  shape.mainCity = text(50, "Main address city", mainRequired);
  shape.mainPostalCode = text(20, "Main address postal code", mainRequired);
  shape.mainStateOrProvinceCode = text(50, "Main address region", false);
  shape.mainCountryCode = text(50, "Main address country", mainRequired);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={Yup.object(shape)}
      onSubmit={(values, { setSubmitting }) => {
        setSubmissionError(null);
        const profile: CustomerProfile = {};
        if (asks(config, "name")) profile.name = values.name.trim();
        if (asks(config, "companyName")) {
          profile.companyName = values.companyName.trim();
        }
        if (asks(config, "address")) {
          profile.address = addressFrom(
            values.streetLine,
            values.streetLine2,
            values.city,
            values.postalCode,
            values.stateOrProvinceCode,
            values.countryCode,
          );
        }
        if (asks(config, "mainAddress")) {
          profile.mainAddress = addressFrom(
            values.mainStreetLine,
            "",
            values.mainCity,
            values.mainPostalCode,
            values.mainStateOrProvinceCode,
            values.mainCountryCode,
          );
        }
        if (asks(config, "website")) profile.website = values.website.trim();
        if (asks(config, "telephone")) {
          profile.telephone = values.telephone.trim();
        }
        if (asks(config, "cellphone")) {
          profile.cellphone = values.cellphone.trim();
        }
        if (asks(config, "fax")) profile.fax = values.fax.trim();

        return Promise.resolve(props.onSubmit(profile))
          .catch((error: Error) =>
            setSubmissionError(
              error?.message || "Something went wrong saving your details.",
            ),
          )
          .finally(() => setSubmitting(false));
      }}
    >
      {(formik) => (
        <Form>
          {props.fieldsHeading}
          {initial?.email && (
            <div className="mb-4 text-sm text-gray-700">
              Signed in as <span className="font-medium">{initial.email}</span>.
              Contact us if this needs to change.
            </div>
          )}

          {asks(config, "name") && (
            <FormTextInput
              label={CUSTOMER_FIELD_LABELS.name}
              name="name"
              type="text"
            />
          )}
          {asks(config, "companyName") && (
            <FormTextInput
              label={CUSTOMER_FIELD_LABELS.companyName}
              name="companyName"
              type="text"
            />
          )}

          {asks(config, "address") && (
            <>
              <FormTextInput label="Address" name="streetLine" type="text" />
              <FormTextInput
                label="Address line 2 (optional)"
                name="streetLine2"
                type="text"
                placeholder="Apartment, suite, unit, building, floor, etc."
              />
              <FormTextInput label="City" name="city" type="text" />
              <FormTextInput
                label="Postal code"
                name="postalCode"
                type="text"
              />
              <FormTextInput
                label="State, Province or Region"
                name="stateOrProvinceCode"
                type="text"
              />
              <FormTextInput label="Country" name="countryCode" type="text" />
            </>
          )}

          {asks(config, "telephone") && (
            <FormTextInput
              label={CUSTOMER_FIELD_LABELS.telephone}
              name="telephone"
              type="tel"
            />
          )}
          {asks(config, "cellphone") && (
            <FormTextInput
              label={CUSTOMER_FIELD_LABELS.cellphone}
              name="cellphone"
              type="tel"
            />
          )}
          {asks(config, "fax") && (
            <FormTextInput
              label={CUSTOMER_FIELD_LABELS.fax}
              name="fax"
              type="tel"
            />
          )}
          {asks(config, "website") && (
            <FormTextInput
              label={CUSTOMER_FIELD_LABELS.website}
              name="website"
              type="text"
              placeholder="example.com"
            />
          )}

          {asks(config, "mainAddress") && (
            <>
              <p className="mb-2 mt-2 text-sm text-gray-700">
                {CUSTOMER_FIELD_LABELS.mainAddress}
                {requires(config, "mainAddress") ? "" : " (optional)"} — leave
                blank if it is the same as above. Shipping still uses the
                address above.
              </p>
              <FormTextInput
                label="Main address"
                name="mainStreetLine"
                type="text"
              />
              <FormTextInput label="City" name="mainCity" type="text" />
              <FormTextInput
                label="Postal code"
                name="mainPostalCode"
                type="text"
              />
              <FormTextInput
                label="State, Province or Region"
                name="mainStateOrProvinceCode"
                type="text"
              />
              <FormTextInput
                label="Country"
                name="mainCountryCode"
                type="text"
              />
            </>
          )}

          <button
            type="submit"
            className="py-2 bg-slate-800 block w-full rounded text-[#f1f5f9] disabled:bg-slate-600"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting
              ? "Saving…"
              : props.submitLabel || "Save changes"}
          </button>
          {props.onSkip && (
            <button
              type="button"
              onClick={() => props.onSkip?.()}
              className="mt-3 block w-full text-center text-sm font-medium text-slate-700 underline"
            >
              {props.skipLabel || "Skip for now — go to the platform"}
            </button>
          )}
          <div className="text-sm text-[#b91c1c] mt-3 min-h-[1.8em]">
            {submissionError || ""}
          </div>
        </Form>
      )}
    </Formik>
  );
}
